import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, Phone } from "lucide-react";
import FadeIn from "@/components/FadeIn";
import SkylineDivider from "@/components/SkylineDivider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Visit() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received — we'll respond personally.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="visit-page">
      <section className="container-x pt-12 lg:pt-24 pb-16" data-testid="contact-hero">
        <FadeIn><p className="overline mb-5">Contact</p></FadeIn>
        <FadeIn delay={120}>
          <h1 className="font-serif text-5xl lg:text-7xl text-moss font-light leading-[1.02] tracking-tight max-w-4xl">
            Drop us a line.
            <br />
            <em className="not-italic text-clay">We read every one.</em>
          </h1>
        </FadeIn>
        <FadeIn delay={240}>
          <p className="mt-8 max-w-2xl text-lg text-coal/70 leading-[1.85]">
            For cottage stays, please use the inquiry form on the Stay page. For Shadow Foundation
            applications on behalf of a family member, colleague, or yourself, use the form on the
            Shadow Foundation page. For wellness offerings, retreats, and group inquiries, use
            the form on the New Leaf page. For anything else — including questions, partnership
            ideas, or just to say hello — leave a message below.
          </p>
        </FadeIn>
      </section>

      <SkylineDivider variant="moss" height={90} opacity={0.5} className="bg-cream" data-testid="contact-skyline-divider" />

      <section className="container-x pt-16 lg:pt-24 pb-24 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20" data-testid="contact-info-grid">
        {/* CONTACT INFO */}
        <div className="lg:col-span-5 space-y-12">
          <FadeIn>
            <div className="flex gap-5 items-start">
              <MapPin size={24} strokeWidth={1.3} className="text-clay flex-shrink-0 mt-1" />
              <div>
                <p className="overline mb-2">Where</p>
                <p className="text-coal/80 text-lg leading-[1.8]">
                  Kempton, Pennsylvania<br />
                  Near Hawk Mountain Sanctuary<br />
                  & the Appalachian Trail
                </p>
                <p className="mt-3 text-coal/60 text-sm">Exact address shared after booking confirmation.</p>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={120}>
            <div className="flex gap-5 items-start">
              <Phone size={24} strokeWidth={1.3} className="text-clay flex-shrink-0 mt-1" />
              <div>
                <p className="overline mb-2">Call or Text</p>
                <a href="tel:6107097341" className="text-coal text-lg link-underline" data-testid="contact-phone">610-709-7341</a>
                <p className="mt-3 text-coal/60 text-sm">Response within 24 hours.</p>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={240}>
            <div className="flex gap-5 items-start">
              <Mail size={24} strokeWidth={1.3} className="text-clay flex-shrink-0 mt-1" />
              <div>
                <p className="overline mb-2">Email</p>
                <a href="mailto:hello@irontalonfarm.com" className="text-coal text-lg link-underline" data-testid="contact-email">hello@irontalonfarm.com</a>
                <p className="mt-3 text-coal/60 text-sm">Usually the fastest way to reach us.</p>
              </div>
            </div>
          </FadeIn>
        </div>

        {/* CONTACT FORM */}
        <div className="lg:col-span-7 lg:pl-8">
          <FadeIn>
            <p className="overline mb-5">Send a note</p>
            <h2 className="font-serif text-4xl lg:text-5xl text-moss font-light leading-tight tracking-tight">
              We read every message.
            </h2>
          </FadeIn>

          <FadeIn delay={140}>
            <form onSubmit={onSubmit} className="mt-12 space-y-10" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div>
                  <label className="label-clean">Your name</label>
                  <input required name="name" value={form.name} onChange={onChange} className="input-clean" data-testid="contact-name-input" />
                </div>
                <div>
                  <label className="label-clean">Email</label>
                  <input required type="email" name="email" value={form.email} onChange={onChange} className="input-clean" data-testid="contact-email-input" />
                </div>
                <div className="md:col-span-2">
                  <label className="label-clean">Subject (optional)</label>
                  <input name="subject" value={form.subject} onChange={onChange} className="input-clean" data-testid="contact-subject-input" />
                </div>
                <div className="md:col-span-2">
                  <label className="label-clean">Your message</label>
                  <textarea required name="message" rows={6} value={form.message} onChange={onChange} className="input-clean resize-none" data-testid="contact-message-input" />
                </div>
              </div>
              <button type="submit" disabled={loading} className="btn-primary disabled:opacity-50" data-testid="contact-submit-btn">
                {loading ? "Sending…" : "Send message"}
              </button>
            </form>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
